class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.interceptNativeAddToCart();

    // The custom header refreshes its cart count on this event (see sections/header.liquid).
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => document.dispatchEvent(new CustomEvent('cart:updated')));

    // GoKwik opens its own checkout modal; close the drawer so it can't sit on top of it.
    this.addEventListener('click', (event) => {
      const gokwikButton = event.target.closest('.gokwik-checkout button');
      if (gokwikButton && !gokwikButton.disabled) this.close();
    });
  }

  // Custom sections that post straight to /cart/add (plain forms or /cart/add?id= links) would otherwise
  // redirect to the cart page. <product-form> handles its own submits, so it is skipped here.
  interceptNativeAddToCart() {
    const isCartAddPath = (url) => /\/cart\/add\/?$/.test(new URL(url, window.location.origin).pathname);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (event.defaultPrevented || !(form instanceof HTMLFormElement) || form.closest('product-form')) return;
      if (!isCartAddPath(form.getAttribute('action') || '')) return;

      event.preventDefault();
      const formData = new FormData(form);
      // Drawer upsell forms name the variant "upsell_id": the PDP pack picker updates the first input[name="id"]
      // on the page, and the drawer renders before the page content.
      if (formData.has('upsell_id')) {
        formData.set('id', formData.get('upsell_id'));
        formData.delete('upsell_id');
      }
      this.addToCart(formData, event.submitter || form.querySelector('[type="submit"]'));
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="/cart/add"]');
      if (event.defaultPrevented || !link || !isCartAddPath(link.href)) return;

      const params = new URL(link.href, window.location.origin).searchParams;
      if (!params.get('id')) return;

      event.preventDefault();
      const formData = new FormData();
      formData.append('id', params.get('id'));
      formData.append('quantity', params.get('quantity') || 1);
      this.addToCart(formData, link);
    });
  }

  addToCart(formData, triggeredBy) {
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    this.setActiveElement(triggeredBy);
    if (triggeredBy) triggeredBy.classList.add('is-loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          alert(response.description || window.cartStrings.error);
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer',
          productVariantId: formData.get('id'),
          cartData: response,
        });
        this.classList.remove('is-empty');
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (triggeredBy) triggeredBy.classList.remove('is-loading');
      });
  }

  setHeaderCartIconAccessibility() {
    // The custom header (sections/header.liquid) renders separate desktop and mobile cart links.
    const triggers = '#cart-icon-bubble, [data-cart-drawer-trigger], .custom-cart-btn, .mobile-cart-wrap';
    document.querySelectorAll(triggers).forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // The PDP "Select Pack" popup (custom-product-page) would otherwise stay open behind the drawer.
    document.getElementById('sticky-popup')?.classList.remove('active');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // The custom header has no #cart-icon-bubble; cart.js throws on sections whose element is missing.
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ].filter((section) => document.getElementById(section.id));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
